import { useQuery } from '@tanstack/react-query';
import { type ContentConfig, fetchContentConfig } from '../services/ContentConfigService';

interface UseContentConfigOptions {
  baseUrl?: string;
}

interface UseContentConfigReturn {
  data: ContentConfig | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const FALLBACK_CONFIG: ContentConfig = {
  content: {
    providerSchedulingPrefix: 'I would like to schedule an appointment with ',
    greetingMessage: {
      greeting: 'Hi',
      subtitle: "I'm UnitedHealthcare's AI assistant.\nWhat do you need help with today?",
      disclaimer:
        'Disclaimer: The AI generated summaries does NOT intend to provide medical advice, diagnose, treat, cure, or prevent any disease or condition for the user. Please use your own independent clinical judgment to confirm accuracy and relevance. Do not rely on AI outputs alone.',
      learnMoreText: 'Learn more >',
      learnMoreAccessibilityLabel: 'Learn more about AI generated summaries',
    },
    banner: {
      title: 'Avery',
      closeAriaLabel: 'Close chat',
      infoAriaLabel: 'Info',
      endChatDialog: {
        title: 'End this chat?',
        message: "Your conversation won't be saved — you'll need to start over when you return.",
        confirmButton: 'Yes, end chat',
        cancelButton: 'Cancel',
        confirmAriaLabel: 'Yes, end this chat',
        cancelAriaLabel: 'Cancel and return to the chat',
      },
    },
    footer: {
      inputPlaceholder: 'Type your message...',
      inputAriaLabel: 'Type your message...',
      sendButtonAriaLabel: 'Send message',
      disclaimer:
        'AI responses are for informational purposes only and not a substitute for professional medical advice.',
    },
    promptOptions: [
      {
        identifier: 'claim',
        title: 'Show recent claims',
      },
    ],
    appointmentBookingForm: {
      title: 'Need few additional details',
      submitButtonTitle: 'Submit',
      cancelButtonTitle: 'Cancel',
      fields: [
        {
          fieldName: 'firstName',
          label: 'First Name',
          type: 'input',
          validations: [
            {
              regEx: '^(?!\\s*$).+',
              message: 'First name is required',
            },
            {
              regEx: '^[A-Za-z\\s]+$',
              message: 'Numbers and punctuation not allowed',
            },
          ],
        },
        {
          fieldName: 'lastName',
          label: 'Last Name',
          type: 'input',
          validations: [
            {
              regEx: '^(?!\\s*$).+',
              message: 'Last name is required',
            },
            {
              regEx: '^[A-Za-z\\s]+$',
              message: 'Numbers and punctuation not allowed',
            },
          ],
        },
        {
          fieldName: 'email',
          label: 'Email',
          type: 'input',
          validations: [
            {
              regEx: '^(?!\\s*$).+',
              message: 'Email is required',
            },
            {
              regEx: '^\\w+([.-]?\\w+)*@\\w+([.-]?\\w+)*(\\.\\w{2,3})+$',
              message: 'Please enter a valid email address',
            },
          ],
        },
        {
          fieldName: 'dob',
          label: 'Date of Birth',
          type: 'date',
          validations: [
            {
              regEx: '^(?!\\s*$).+',
              message: 'Date of birth is required',
            },
            {
              regEx: '^(0?[1-9]|1[0-2])\\/(0?[1-9]|[12][0-9]|3[01])\\/([0-9]{4})$',
              message: 'Please enter date of birth in MM/DD/YYYY format',
            },
          ],
        },
        {
          fieldName: 'state',
          label: 'State',
          placeholder: 'Select a state',
          type: 'select',
          validations: [
            {
              regEx: '^(?!\\s*$).+',
              message: 'Select a State',
            },
          ],
        },
      ],
    },
    errors: {
      network: {
        title: 'Connection Issue',
        message:
          "We're having trouble connecting. Please check your internet connection and try again.",
        retryButton: 'Try Again',
      },
      session: {
        title: 'Session Expired',
        message: 'Your session has expired. Please refresh the page to continue.',
        refreshButton: 'Refresh',
      },
      generic: {
        title: 'Something Went Wrong',
        message:
          'We encountered an unexpected error. Please try again later or contact support if the problem persists.',
        retryButton: 'Try Again',
        supportButton: 'Contact Support',
      },
      consent: {
        title: 'Consent Required',
        message: 'You need to provide consent to access this service.',
        consentButton: 'Provide Consent',
      },
      notFound: {
        title: 'Page Not Found',
        message: "The page you're looking for doesn't exist.",
        homeButton: 'Go Home',
      },
    },
  },
  featureFlags: {
    showTimestamps: false,
    showWelcomeBanner: false,
    bannerInfoButton: {
      enabled: false,
      visible: false,
    },
  },
};

export const useContentConfig = ({
  baseUrl,
}: UseContentConfigOptions = {}): UseContentConfigReturn => {
  const query = useQuery({
    queryKey: ['content-config', baseUrl ?? 'default'],
    queryFn: () => fetchContentConfig({ baseUrl }),
    staleTime: Infinity, // Never refetch once loaded
    gcTime: Infinity, // Keep in cache forever
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Shared across all consumers — fetch only once
    refetchOnReconnect: false,
    retry: 1,
  });

  return {
    // Fall back to bundled config if the request fails.
    data: query.data ?? (query.isError ? FALLBACK_CONFIG : null),
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error : null,
    refetch: async () => {
      await query.refetch();
    },
  };
};
